import { Platform } from "react-native";

/**
 * The platform's minimum touch target, and how much slop a control needs to reach it.
 *
 * The floor is 44pt on iOS and 48dp on Android. A per-call-site literal, such as a flat `4` of slop
 * on a 36pt control, reaches 44 on both and is 4dp short on Android while looking deliberate in
 * review, so the floor lives here once and the arithmetic is a function.
 *
 * Callers apply the result as `hitSlop`, not padding. Slop extends the touch area without changing
 * the laid-out frame, so a 36pt disc stays 36pt inside the glass capsule iOS 26 draws around it
 * (see `AppBarAction`). Material does the same: an M3 chip paints about 32dp and reaches 48 through
 * its touch target.
 *
 * `hitSlop` has no effect on web, since `react-native-web`'s `Pressable` ignores it, so a web
 * control must reach the floor with its frame (`appBarTouchFrame()` does this for app-bar actions).
 * This still returns 44 on web so a caller sizing a frame gets the right number.
 *
 * A function rather than a const because a module-level `Platform.OS` read freezes at import time,
 * and the tests switch `Platform.OS` per case.
 */
export function touchFloor(): number {
  return Platform.OS === "android" ? 48 : 44;
}

/**
 * The `hitSlop` a control that paints `painted` points needs on each side to reach the floor.
 *
 * Rounded up, so the floor is met rather than approached: a 36pt control returns 4 on iOS (36+8=44)
 * and 6 on Android (36+12=48). Zero when the paint already clears the floor.
 *
 * In a wrapping row, overlapping slop is a known trade-off. RN clips slop to the parent and
 * hit-tests the topmost view where two slop regions overlap, so on a row with `gap-2` (7pt, since
 * gaps still use the rem ramp) Android's 6 crosses into the neighbouring row by 5pt. A 5pt band
 * where the lower row wins is better than a target that misses the floor everywhere.
 */
export function slopToFloor(painted: number): number {
  return Math.max(0, Math.ceil((touchFloor() - painted) / 2));
}

/**
 * The height a bare text link paints: a `text-sm` line and its leading, rounded up. Named here
 * rather than per screen so the several text links across the auth flow share one value.
 */
export const TEXT_LINK_PAINT = 20;
